import { Button, Divider } from "@jewlr/storybook/core"
import { useAriaHidden } from "@jewlr/storybook/helpers"
import loadable from "@loadable/component"
import { FocusScope } from "@react-aria/focus"
import { AnimatePresence } from "framer-motion"
import PropTypes from "prop-types"
import { useContext, useRef } from "react"
import ScrollLock, {
  TouchScrollable,
} from "react-scrolllock-with-horizontal-scroll"
import styled from "styled-components"

import withHeader from "areas/header/hoc/with-header"
import { HeaderMenuContext } from "areas/header/portal"
import { retry } from "helpers/application"
import { useNavigation } from "helpers/use-navigation"

import { Nav, NavList, SideMenu, SideMenuContent } from "../components"
import OverLay from "../overlay"

const CountrySwitcher = loadable(() =>
  retry(() => import("./country-switcher"))
)
const MobileNav = loadable(() => retry(() => import("./mobile-nav")))
const MobileSubmenu = loadable(() => retry(() => import("./mobile-submenu")))

const AnchorButton = styled(Button).attrs((props) => ({
  bg: "primary",
  color: "white",
  fontSize: "16px",
  fontWeight: "700",
  forwardedAs: "a",
  height: "44px",
  mt: 3,
  textTransform: "uppercase",
  ...props,
}))``

const MobileMenuContent = ({ showMenu, toggleMenu }) => {
  const { menu } = useContext(HeaderMenuContext)
  const menuRef = useRef()
  const keyboardProps = useNavigation({
    onEscape: () => toggleMenu(showMenu),
  })

  const menuItems = menu && menu.categories
  const navBarLinks = menu?.navbar_links?.filter((links) => !links.external)
  const externalLinks = menu?.navbar_links?.filter((links) => links.external)

  useAriaHidden({ ref: menuRef })

  return (
    <SideMenu
      id="mobile-menu"
      ref={menuRef}
      showMenu={showMenu}
      {...keyboardProps}
    >
      <SideMenuContent>
        <Nav as="nav">
          <NavList aria-label="Main Navigation" role="menu">
            {menuItems?.length > 0 && <MobileNav items={menuItems} />}
            <Divider bg="blackPrint" my={0} />
            {navBarLinks?.length > 0 && <MobileNav items={navBarLinks} />}
            <CountrySwitcher />
            {externalLinks?.map((link, i) => (
              <li
                data-cy={`nav-mobile-${link.name}`}
                key={`external-link-${i}`}
                role="none"
              >
                <AnchorButton
                  data-lc="menu-mobile"
                  href={link.url}
                  rel="noopener noreferrer"
                  role="menuitem"
                  target="_blank"
                >
                  {link.name}
                </AnchorButton>
              </li>
            ))}
          </NavList>
        </Nav>
      </SideMenuContent>
      <MobileSubmenu />
    </SideMenu>
  )
}

MobileMenuContent.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
}

const InternalMobileMenu = ({ showMenu, toggleMenu }) => (
  <AnimatePresence>
    {showMenu && (
      <>
        <FocusScope autoFocus contain restoreFocus>
          <ScrollLock isActive={showMenu}>
            <TouchScrollable>
              <MobileMenuContent showMenu={showMenu} toggleMenu={toggleMenu} />
            </TouchScrollable>
          </ScrollLock>
        </FocusScope>
        <OverLay />
      </>
    )}
  </AnimatePresence>
)

InternalMobileMenu.propTypes = {
  showMenu: PropTypes.bool,
  toggleMenu: PropTypes.func,
}

export default withHeader(InternalMobileMenu)
